import { MediaItem } from '../../types';

/**
 * Fills gaps between media items by shifting them leftward (earlier in time)
 * to remove any empty space between consecutive items.
 * 
 * Hidden items (hidden: true) and archived items (effect: 'archived') are ignored 
 * during gap filling and remain in their original positions.
 * 
 * Algorithm:
 * 1. Filter out hidden and archived items
 * 2. Sort visible items by start time
 * 3. For each visible item, calculate accumulated shift from all previous gaps
 * 4. Apply shift to move visible items left, closing gaps
 * 5. Add hidden/archived items back to the result
 * 
 * @param mediaItems - Array of media items to process
 * @returns New array of media items with gaps filled (hidden/archived items unchanged)
 */
export const fillMediaItemGaps = (
  mediaItems: MediaItem[]
): MediaItem[] => {
  // Handle edge cases
  if (!mediaItems || mediaItems.length === 0) {
    return mediaItems;
  }

  // ✅ Separate hidden/archived items from visible items
  const excludedItems = mediaItems.filter(item => 
    item.hidden === true || item.effect === 'archived'
  );
  const visibleItems = mediaItems.filter(item => 
    item.hidden !== true && item.effect !== 'archived'
  );

  // If no visible items, return original array
  if (visibleItems.length === 0) {
    return mediaItems;
  }

  // If only one visible item, move it to start at 0 if not already
  if (visibleItems.length === 1) {
    const item = visibleItems[0];
    if (item.start === 0) {
      return mediaItems; // No changes needed
    }
    const currentDuration = item.end - item.start;
    const updatedItem = {
      ...item,
      start: 0,
      end: currentDuration
    };
    // Return updated visible item + all excluded items
    return [...excludedItems, updatedItem];
  }

  // Phase 1: Sort visible items by start time
  const sortedItems = [...visibleItems].sort((a, b) => a.start - b.start);

  // Phase 2 & 3: Calculate shifts and apply them to visible items only
  // Initialize shift with first item's start time to move everything to begin at 0
  let shiftAmount = sortedItems[0].start;
  const newVisibleItems: MediaItem[] = [];

  for (let i = 0; i < sortedItems.length; i++) {
    const item = sortedItems[i];
    
    // Calculate the current duration (may be trimmed from original length)
    const currentDuration = item.end - item.start;
    
    // Apply accumulated shift to this item
    const newStart = Math.max(0, item.start - shiftAmount);
    const newEnd = newStart + currentDuration; // Preserve current duration
    
    const newItem: MediaItem = {
      ...item,
      start: newStart,
      end: newEnd
    };
    
    newVisibleItems.push(newItem);
    
    // Calculate gap to next item (if exists)
    if (i < sortedItems.length - 1) {
      const nextStart = sortedItems[i + 1].start;
      const currentEnd = item.end; // Use original end for gap calculation
      
      // If there's a gap, accumulate it for subsequent items
      if (nextStart > currentEnd) {
        const gap = nextStart - currentEnd;
        shiftAmount += gap;
      }
    }
  }

  // Phase 4: Combine processed visible items with unchanged hidden/archived items
  return [...newVisibleItems, ...excludedItems];
};
